// Export Dropdown Component
// Provides dropdown menu for exporting notes in multiple formats

import { Dropdown } from 'antd';
import Icon from './Icon';
import {
    exportAsTxt,
    exportAsJson,
    exportAsMarkdown,
    exportAsPdf,
    exportAsDocx,
} from '../../utils/exportNote';
import './ExportDropdown.css';

/**
 * ExportDropdown component - Dropdown menu for exporting notes
 * @param {object} note - The note object to export
 * @param {string} placement - Dropdown placement (default: 'bottomRight')
 * @param {boolean} showLabel - Whether to show "Export" label (default: false)
 * @param {string} size - Icon size: 'sm', 'md', 'lg' (default: 'sm')
 * @param {string} className - Additional CSS class names
 */
export default function ExportDropdown({
    note,
    placement = 'bottomRight',
    showLabel = false,
    size = 'sm',
    className = '',
}) {
    if (!note) return null;

    const handleExport = async (format) => {
        try {
            switch (format) {
                case 'txt':
                    exportAsTxt(note);
                    break;
                case 'json':
                    exportAsJson(note);
                    break;
                case 'markdown':
                    exportAsMarkdown(note);
                    break;
                case 'pdf':
                    exportAsPdf(note);
                    break;
                case 'docx':
                    await exportAsDocx(note);
                    break;
                default:
                    console.error('Unknown format:', format);
            }
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const menuItems = [
        {
            key: 'txt',
            label: (
                <span className="export-menu-item">
                    <Icon name="FileText" size="sm" />
                    <span>Plain Text (.txt)</span>
                </span>
            ),
            onClick: () => handleExport('txt'),
        },
        {
            key: 'json',
            label: (
                <span className="export-menu-item">
                    <Icon name="Braces" size="sm" />
                    <span>JSON (.json)</span>
                </span>
            ),
            onClick: () => handleExport('json'),
        },
        {
            key: 'markdown',
            label: (
                <span className="export-menu-item">
                    <Icon name="Hash" size="sm" />
                    <span>Markdown (.md)</span>
                </span>
            ),
            onClick: () => handleExport('markdown'),
        },
        {
            type: 'divider',
        },
        {
            key: 'pdf',
            label: (
                <span className="export-menu-item">
                    <Icon name="FileType" size="sm" />
                    <span>PDF Document (.pdf)</span>
                </span>
            ),
            onClick: () => handleExport('pdf'),
        },
        {
            key: 'docx',
            label: (
                <span className="export-menu-item">
                    <Icon name="FileText" size="sm" />
                    <span>Word Document (.docx)</span>
                </span>
            ),
            onClick: () => handleExport('docx'),
        },
    ];

    const handleClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement={placement}
            overlayClassName="export-dropdown-overlay"
        >
            <button
                className={`export-dropdown-trigger ${className}`.trim()}
                onClick={handleClick}
                title="Export note"
            >
                <Icon name="Download" size={size} />
                {showLabel && <span className="export-label">Export</span>}
            </button>
        </Dropdown>
    );
}
